import { AdminEmailHtml, AdminEmailTemplateHtml } from '@platform/client/schemas';
import { useCallback, useMemo, useRef, useState } from 'react';

type EmailPreviewProps = {
  html: AdminEmailHtml | AdminEmailTemplateHtml;
};

const neutralizeLinks = (html: AdminEmailHtml | AdminEmailTemplateHtml): string => {
  if (!html) return '';
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');

  const style = doc.createElement('style');
  style.textContent = `a { pointer-events: none !important; cursor: default !important;}`;
  doc.head.appendChild(style);

  return doc.documentElement.outerHTML;
};

export const EmailPreview = ({ html }: EmailPreviewProps) => {
  const safeHtml = useMemo(() => neutralizeLinks(html), [html]);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [height, setHeight] = useState(0);

  // Adjust the height of the iframe to fit the content
  const handleLoad = useCallback(() => {
    const doc = iframeRef.current?.contentDocument;
    if (doc?.documentElement) {
      setHeight(doc.documentElement.scrollHeight);
    }
  }, []);

  return (
    <iframe
      ref={iframeRef}
      srcDoc={safeHtml}
      // DO NOT ADD allow-scripts to sandbox
      // allow-same-origin is necessary for the iframe to access its contentDocument to auto adjust
      // height for email content, overriding scripts in this iframe exposes the content to potential risks
      sandbox='allow-same-origin'
      className='shadow-sm border border-gray-200 rounded-lg min-w-[600px]'
      style={{ height: height || '0px', width: '100%' }}
      onLoad={handleLoad}
      title='Email preview'
    />
  );
};
